/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState } from 'react';
import { API_BASE_URL } from '../utils/apiBaseUrl.ts';
import { getAuth } from 'firebase/auth';

interface SyncAllButtonProps {
  isAdmin: boolean;
}

interface SyncResult {
  shopify?: {
    success: boolean;
    totalCount: number;
    error?: string;
  };
  printful?: {
    success: boolean;
    totalCount: number;
    error?: string;
  };
  collections?: {
    success: boolean;
    totalCount: number;
    error?: string;
  };
}

export default function SyncAllButton({ isAdmin }: SyncAllButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>('');
  const [results, setResults] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const syncEndpoint = async (endpoint: string, name: string) => {
    setCurrentStep(`Syncing ${name}...`);
    
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Not authenticated');
    }

    const idToken = await user.getIdToken();
    const url = `${API_BASE_URL}/.netlify/functions/${endpoint}`;
    
    console.log(`Calling ${name} sync at: ${url}`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    console.log(`${name} sync response status:`, response.status);
    console.log(`${name} sync response headers:`, Object.fromEntries(response.headers.entries()));

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error(`${name} sync returned non-JSON response:`, text.substring(0, 200));
      throw new Error(`${name} sync failed: Server returned ${response.status}. Check function logs.`);
    }

    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      console.error(`${name} sync JSON parse error:`, jsonError);
      throw new Error(`${name} sync failed: Invalid response format`);
    }

    if (!response.ok) {
      throw new Error(data.error || `${name} sync failed with status ${response.status}`);
    }

    return data;
  };

  const handleSyncAll = async () => {
    setIsLoading(true);
    setResults(null);
    setError(null);
    setCurrentStep('Starting sync...');

    const syncResults: SyncResult = {};

    try {
      // Sync Collections first (fastest, needed for UI)
      try {
        const collectionsData = await syncEndpoint('collections-sync', 'Collections');
        syncResults.collections = {
          success: true,
          totalCount: collectionsData.totalCount,
        };
      } catch (err) {
        syncResults.collections = {
          success: false,
          totalCount: 0,
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      }

      // Sync Shopify and Printful in parallel (they're independent)
      setCurrentStep('Syncing Shopify and Printful orders...');
      
      const [shopifyResult, printfulResult] = await Promise.allSettled([
        syncEndpoint('shopify-manual-sync', 'Shopify'),
        syncEndpoint('printful-manual-sync', 'Printful'),
      ]);

      // Process Shopify result
      if (shopifyResult.status === 'fulfilled') {
        syncResults.shopify = {
          success: true,
          totalCount: shopifyResult.value.totalCount,
        };
      } else {
        syncResults.shopify = {
          success: false,
          totalCount: 0,
          error: shopifyResult.reason?.message || 'Unknown error',
        };
      }

      // Process Printful result
      if (printfulResult.status === 'fulfilled') {
        syncResults.printful = {
          success: true,
          totalCount: printfulResult.value.totalCount,
        };
      } else {
        syncResults.printful = {
          success: false,
          totalCount: 0,
          error: printfulResult.reason?.message || 'Unknown error',
        };
      }

      setResults(syncResults);
      setCurrentStep('Sync completed!');

      // Check if any syncs failed
      const failures = Object.entries(syncResults).filter(([_, result]) => !result.success);
      if (failures.length > 0) {
        setError(`${failures.length} sync(s) failed. See details below.`);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during sync');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const allSuccessful = results && 
    results.shopify?.success && 
    results.printful?.success && 
    results.collections?.success;

  return (
    <div>

      <button
        onClick={handleSyncAll}
        disabled={isLoading}
      >
        {isLoading ? '⏳ Syncing...' : '🔄 Sync All Data'}
      </button>

      {isLoading && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <p className="text-blue-800 font-semibold">{currentStep}</p>
          <p className="text-xs text-blue-600 mt-1">Please don't close this page.</p>
        </div>
      )}

      {results && (
        <div className="mt-4 space-y-2">
          {allSuccessful && (
            <div className="p-3 bg-green-50 border border-green-200 rounded">
              <p className="text-green-800 font-semibold">✅ All Syncs Completed Successfully!</p>
            </div>
          )}

          {/* Collections Result */}
          {results.collections && (
            <div className={`p-3 border rounded ${
              results.collections.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`font-semibold ${
                results.collections.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {results.collections.success ? '✅' : '❌'} Collections
              </p>
              {results.collections.success ? (
                <p className="text-sm text-green-700 mt-1">
                  {results.collections.totalCount} collections synced
                </p>
              ) : (
                <p className="text-sm text-red-700 mt-1">
                  {results.collections.error}
                </p>
              )}
            </div>
          )}

          {/* Shopify Result */}
          {results.shopify && (
            <div className={`p-3 border rounded ${
              results.shopify.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`font-semibold ${
                results.shopify.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {results.shopify.success ? '✅' : '❌'} Shopify Orders
              </p>
              {results.shopify.success ? (
                <p className="text-sm text-green-700 mt-1">
                  {results.shopify.totalCount} orders synced
                </p>
              ) : (
                <p className="text-sm text-red-700 mt-1">
                  {results.shopify.error}
                </p>
              )}
            </div>
          )}

          {/* Printful Result */}
          {results.printful && (
            <div className={`p-3 border rounded ${
              results.printful.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`font-semibold ${
                results.printful.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {results.printful.success ? '✅' : '❌'} Printful Orders
              </p>
              {results.printful.success ? (
                <p className="text-sm text-green-700 mt-1">
                  {results.printful.totalCount} orders synced
                </p>
              ) : (
                <p className="text-sm text-red-700 mt-1">
                  {results.printful.error}
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-yellow-800 font-semibold">⚠️ Warning</p>
              <p className="text-sm text-yellow-700 mt-1">{error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}